import {
    PropsWithChildren,
    createContext,
    useContext,
    Suspense,
    useState
} from "react";

import { Initializing } from "./Initializing";
import { ConfigClient } from "../clients/ConfigClient";
import i18n, { resources } from "../i18n";
import { Config, Tenant } from "../types/Config";
import { httpClient } from "../utils/http";
import { wrapPromise } from "../utils/suspender";

const getConfiguration = async (): Promise<Config> => {
    const configClient = new ConfigClient(httpClient);
    const config = await configClient.setup();

    localStorage.setItem("tenant", config.tenant);
    localStorage.setItem("language", config.language);

    await i18n.init({
        resources: resources,
        debug: import.meta.env.DEV,
        interpolation: {
            escapeValue: false
        },
        fallbackLng:
            config.language ?? import.meta.env.VITE_FALLBACK_LANGUAGE ?? "de",
        supportedLngs: config.supportedLanguages
    });

    return config;
};

const suspendableConfig = wrapPromise(getConfiguration());

const initialConfig: Config = {
    tenant: (localStorage.getItem("tenant") as Tenant | null) ?? "kkiosk",
    store: { id: "", country: "" },
    selfCheckoutTerminalId: "",
    featuredProducts: [],
    productPresets: [],
    kitchen: {
        hasKitchen: false
    },
    couponCodes: [],
    isEnabled: false,
    language: "",
    supportedLanguages: [],
    cartCheckEnabled: false
};

type ConfigContext = Config & {
    reload: () => void;
};

const ConfigContext = createContext<ConfigContext>({
    ...initialConfig,
    reload: () => {}
});

const cleanup = (config: Config): Config => {
    const cleanedPresets = config.productPresets
        .map((pp) => ({
            ...pp,
            categories: pp.categories.filter((c) => c.products.length > 0)
        }))
        .filter((pp) => pp.categories.length > 0);

    return { ...config, productPresets: cleanedPresets };
};

const InternalProvider = ({ children }: PropsWithChildren) => {
    const config = suspendableConfig.read();

    const reload = () => {
        getConfiguration().then((config) =>
            setConfigCtx({ ...cleanup(config), reload })
        );
    };

    const [configCtx, setConfigCtx] = useState<ConfigContext>({
        ...cleanup(config),
        reload
    });

    return (
        <ConfigContext.Provider value={configCtx}>
            <div data-theme={config.tenant} className="flex h-full flex-col">
                {children}
            </div>
        </ConfigContext.Provider>
    );
};

export const ConfigProvider = ({ children }: PropsWithChildren) => {
    return (
        <Suspense fallback={<Initializing tenant={initialConfig.tenant} />}>
            <InternalProvider>{children}</InternalProvider>
        </Suspense>
    );
};

export const useConfig = () => useContext(ConfigContext);
